import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { T } from "@/constants/testIds";

const AGES = [
  { slug: null, label: "All ages" },
  { slug: "4-7", label: "Ages 4–7" },
  { slug: "8-11", label: "Ages 8–11" },
  { slug: "12-15", label: "Ages 12–15" },
];

export default function KidsIndex() {
  const [items, setItems] = useState([]);
  const [age, setAge] = useState(null);
  useEffect(() => {
    const url = age ? `/kids-stories?age_group=${age}` : "/kids-stories";
    api.get(url).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [age]);

  return (
    <div className="theme-kids relative">
      <div className="absolute inset-0 bg-gradient-to-b from-[#FDF5E6] via-background to-background -z-10" aria-hidden />
      <section className="pt-16 sm:pt-24 pb-8">
        <div className="container-editorial">
          <span className="eyebrow text-terracotta">Kids & Families</span>
          <h1 className="heading-display mt-4">Bible stories, beautifully told.</h1>
          <p className="lead mt-6 max-w-2xl">
            A gentle storybook for children — the real events of the Bible in age-appropriate language, with parent
            notes and memory verses. No public comments, no messaging, no unnecessary data collection.
          </p>

          <div className="mt-10 flex flex-wrap gap-2">
            {AGES.map((a) => (
              <button
                key={a.label}
                data-testid={T.kidsAgeFilter(a.slug || "all")}
                onClick={() => setAge(a.slug)}
                className={`px-4 py-2 text-xs uppercase tracking-widest border-2 transition-colors rounded-full ${
                  age === a.slug
                    ? "bg-terracotta text-background border-terracotta"
                    : "border-terracotta/40 text-ink hover:border-terracotta"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-editorial">
          <div data-testid={T.kidsGrid} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((s) => (
              <Link
                key={s.id}
                to={`/kids/${s.slug}`}
                data-testid={T.storybookCard(s.slug)}
                className="storybook-card overflow-hidden flex flex-col"
              >
                {s.image_url && (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={s.image_url} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-terracotta/15 text-terracotta-dark text-[11px] uppercase tracking-widest">
                    Ages {s.age_group}
                  </span>
                  <h3 className="font-serif text-2xl mt-3 text-ink" style={{ fontWeight: 500 }}>{s.title}</h3>
                  <p className="text-sm mt-3 text-ink-muted leading-relaxed">{s.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-editorial">
          <div className="storybook-card p-10 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="heading-3">For parents</h3>
              <p className="body-copy mt-4 text-ink-muted">
                Every story includes a parent note and a memory verse. Read together. Talk together. Pray together.
                For any contact concerning your child, use our parent-guardian contact form.
              </p>
              <Link to="/contact" className="btn-gold mt-6 inline-flex">Parent / Guardian Contact</Link>
            </div>
            <div>
              <h3 className="heading-3">Our promises</h3>
              <ul className="mt-4 space-y-2 body-copy text-ink-muted">
                <li>· No frightening imagery or manipulation.</li>
                <li>· No child-to-adult messaging on this site.</li>
                <li>· No unnecessary data collection about children.</li>
                <li>· Illustrations are marked as illustrations, not historical photographs.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
